import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { TOXBOX_API_BASE } from "./toxboxConfig";
import styles from "./Blog.module.css";

// Helper — Format ISO date to readable string
function formatDate(isoString) {
  if (!isoString) return "";
  try {
    return new Date(isoString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

// MetaUpdater — Dynamically sets <title> and <meta name="description">
function useDocumentMeta(title, description) {
  const originalTitle = useRef(document.title);
  const originalDesc = useRef(
    document.querySelector('meta[name="description"]')?.content || ""
  );

  useEffect(() => {
    if (title) document.title = `${title} — Provaantech Blog`;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (description) {
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.name = "description";
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = description;
    }

    return () => {
      document.title = originalTitle.current;
      if (metaDesc) metaDesc.content = originalDesc.current;
    };
  }, [title, description]);
}

// BlogDetailSkeleton
function BlogDetailSkeleton() {
  return (
    <div className={styles.detailSkeleton} aria-hidden="true">
      <div className={styles.skeletonHero} />
      <div className={styles.detailContainer}>
        <div className={styles.skeletonPill} />
        <div className={styles.skeletonTitleLg} />
        <div className={styles.skeletonLine} />
        <div className={styles.skeletonLine} style={{ width: "60%" }} />
        <div style={{ marginTop: "40px" }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className={styles.skeletonLine}
              style={{ width: `${85 + Math.random() * 15}%`, marginBottom: "12px" }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [blog, setBlog] = useState(() => location.state?.blog || null);
  const [loading, setLoading] = useState(() => !location.state?.blog);
  const [error, setError] = useState("");

  useDocumentMeta(blog?.metaTitle || blog?.title, blog?.metaDescription);

  useEffect(() => {
    if (blog && blog.slug === slug) {
      setLoading(false);
      return;
    }

    if (!slug) {
      setError("Invalid blog URL.");
      setLoading(false);
      return;
    }

    setError("");
    setLoading(true);

    const fetchBlogFromList = async () => {
      try {
        const url = `${TOXBOX_API_BASE}?page=1&limit=50`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);

        const response = await fetch(url, {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }

        const resJson = await response.json();
        const blogList = resJson?.data || [];
        const foundBlog = blogList.find((b) => b.slug === slug);

        if (foundBlog) {
          setBlog(foundBlog);
        } else {
          setError("This blog post could not be found.");
        }
      } catch (err) {
        console.error("[BlogDetailPage] Failed to fetch blog from list:", err);
        if (err.name === "AbortError") {
          setError("The request timed out. Please check your connection.");
        } else {
          setError("Failed to load the blog post. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBlogFromList();
  }, [slug]);

  if (loading) return <BlogDetailSkeleton />;

  if (error) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.detailContainer}>
          <div className={styles.errorState}>
            <div className={styles.errorIcon}>⚠️</div>
            <h2 className={styles.errorTitle}>Blog Not Available</h2>
            <p className={styles.errorMessage}>{error}</p>
            <div className={styles.errorActions}>
              <button
                className={styles.backBtn}
                onClick={() => navigate("/blog")}
              >
                ← Back to Blog
              </button>
              <button
                className={styles.retryBtn}
                onClick={() => {
                  setError("");
                  setLoading(true);
                  setBlog(null);
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasThumbnail = blog.thumbnailUrl && blog.thumbnailUrl.trim() !== "";

  return (
    <article className={styles.detailPage}>
      <div className={styles.detailContainer}>
        <button
          className={styles.backBtn}
          onClick={() => navigate("/blog")}
          aria-label="Back to blog listing"
        >
          ← Back to Blog
        </button>

        {/* ── Hero Thumbnail (Aligned inside container) ── */}
        {hasThumbnail && (
          <div className={styles.detailHero}>
            <img
              src={blog.thumbnailUrl}
              alt={blog.title}
              className={styles.detailHeroImg}
              onError={(e) => {
                e.currentTarget.closest(`.${styles.detailHero}`).style.display = "none";
              }}
            />
          </div>
        )}

        <div className={styles.detailMetaRow}>
          {blog.industry && (
            <span className={styles.industryPill}>{blog.industry}</span>
          )}
          <span className={styles.detailDate}>{formatDate(blog.publishedAt)}</span>
        </div>

        <h1 className={styles.detailTitle}>{blog.title}</h1>

        {blog.metaDescription && (
          <p className={styles.detailSubtitle}>{blog.metaDescription}</p>
        )}

        <hr className={styles.divider} />

        <div
          className={styles.blogContent}
          dangerouslySetInnerHTML={{ __html: blog.htmlContent }}
        />

        {blog.keywords && blog.keywords.length > 0 && (
          <div className={styles.keywordSection}>
            <span className={styles.keywordLabel}>Tags:</span>
            <div className={styles.keywordChips}>
              {blog.keywords.map((kw) => (
                <span key={kw} className={styles.keywordChip}>
                  {kw}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className={styles.detailFooter}>
          <button
            className={styles.backBtn}
            onClick={() => navigate("/blog")}
          >
            ← Back to Blogs
          </button>
        </div>
      </div>
    </article>
  );
}
